import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Plus,
  Search,
} from 'lucide-react';

import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { formatDateOnly } from '@/lib/date-utils';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Badge } from '@/app/components/ui/badge';
import { useComplianceIncidents } from '@/hooks/use-compliance-incidents';
import type {
  ComplianceIncidentFilters,
  ComplianceIncidentSeverity,
  ComplianceIncidentStatus,
} from '@/lib/types';

const STATUS_LABELS: Record<ComplianceIncidentStatus, string> = {
  reported: 'Reported',
  under_investigation: 'Under Investigation',
  corrective_action: 'Corrective Action',
  closed: 'Closed',
};

const STATUS_COLORS: Record<ComplianceIncidentStatus, string> = {
  reported: 'bg-blue-100 text-blue-800',
  under_investigation: 'bg-yellow-100 text-yellow-800',
  corrective_action: 'bg-orange-100 text-orange-800',
  closed: 'bg-green-100 text-green-800',
};

const SEVERITY_LABELS: Record<ComplianceIncidentSeverity, string> = {
  minor: 'Minor',
  moderate: 'Moderate',
  major: 'Major',
  critical: 'Critical',
  fatality: 'Fatality',
};

const SEVERITY_COLORS: Record<ComplianceIncidentSeverity, string> = {
  minor: 'bg-gray-100 text-gray-800',
  moderate: 'bg-yellow-100 text-yellow-800',
  major: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
  fatality: 'bg-red-200 text-red-900',
};

export function ComplianceIncidentsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ComplianceIncidentFilters>({
    page: 1,
    page_size: 20,
  });
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading } = useComplianceIncidents(filters);

  const handleSearch = () => {
    setFilters((prev) => ({ ...prev, q: searchInput, page: 1 }));
  };

  const totalPages = data ? Math.ceil(data.count / (filters.page_size || 20)) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Incident Register</h1>
          <p className="text-muted-foreground">
            Track formal incidents requiring IRATA notification and investigation
          </p>
        </div>
        <Button onClick={() => navigate('/compliance/incidents/new')}>
          <Plus className="mr-2 h-4 w-4" />
          Report Incident
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search incidents..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
              />
            </div>
            <Select
              value={filters.status || 'all'}
              onValueChange={(v) =>
                setFilters((prev) => ({
                  ...prev,
                  status: v === 'all' ? undefined : (v as ComplianceIncidentStatus),
                  page: 1,
                }))
              }
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.severity || 'all'}
              onValueChange={(v) =>
                setFilters((prev) => ({
                  ...prev,
                  severity: v === 'all' ? undefined : (v as ComplianceIncidentSeverity),
                  page: 1,
                }))
              }
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Severities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                {Object.entries(SEVERITY_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : data && data.results.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Incident #</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.results.map((incident) => (
                  <TableRow
                    key={incident.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/compliance/incidents/${incident.id}`)}
                  >
                    <TableCell className="font-mono text-sm">
                      {incident.incident_number}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{incident.title}</span>
                        {incident.has_overdue_notifications && (
                          <Badge variant="destructive" className="flex items-center gap-1 text-xs">
                            <Clock className="h-3 w-3" />
                            Overdue
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{formatDateOnly(incident.date)}</TableCell>
                    <TableCell>
                      <Badge className={SEVERITY_COLORS[incident.severity]}>
                        {SEVERITY_LABELS[incident.severity]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[incident.status]}>
                        {STATUS_LABELS[incident.status]}
                      </Badge>
                    </TableCell>
                    <TableCell>{incident.location}</TableCell>
                    <TableCell>
                      <Link
                        to={`/compliance/incidents/${incident.id}`}
                        className="text-sm text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertTriangle className="mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="text-lg font-semibold">No incidents found</h3>
              <p className="text-muted-foreground">
                {filters.q || filters.status || filters.severity
                  ? 'Try adjusting your filters'
                  : 'Report an incident to get started'}
              </p>
              {!filters.q && !filters.status && !filters.severity && (
                <Button
                  className="mt-4"
                  onClick={() => navigate('/compliance/incidents/new')}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Report Incident
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {((filters.page || 1) - 1) * (filters.page_size || 20) + 1} to{' '}
            {Math.min((filters.page || 1) * (filters.page_size || 20), data.count)} of{' '}
            {data.count} incidents
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous}
              onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <span className="text-sm">
              Page {filters.page || 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next}
              onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))}
            >
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
